import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import type { Json } from '@brix/types'

interface TemplateBlock {
  type: string
  props: Record<string, unknown>
  settings: Record<string, unknown>
  orderIndex: number
}

export interface Template {
  id: string
  name: string
  description: string | null
  category: string
  schema: { blocks: TemplateBlock[] }
}

export function useTemplates() {
  return useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('templates')
        .select('id, name, description, category, schema')
        .eq('is_public', true)
        .order('category')
      if (error) throw error
      return (data ?? []) as unknown as Template[]
    },
  })
}

export function useSaveAsTemplate() {
  const tenant = useAuthStore((s) => s.tenant)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      category,
      blocks,
    }: {
      name: string
      category: string
      blocks: TemplateBlock[]
    }) => {
      if (!tenant) throw new Error('Not authenticated')
      const schema = { blocks }
      const { error } = await supabase.from('templates').insert({
        name,
        category,
        description: null,
        is_public: false,
        tenant_id: tenant.id,
        schema: schema as unknown as Json,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useApplyTemplate(siteId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ pageId, templateId }: { pageId: string; templateId: string }) => {
      // Fetch template
      const { data: tpl, error: te } = await supabase
        .from('templates')
        .select('schema')
        .eq('id', templateId)
        .single()
      if (te ?? !tpl) throw new Error(te?.message ?? 'Template not found')

      const blocks: TemplateBlock[] = (tpl.schema as unknown as { blocks: TemplateBlock[] }).blocks ?? []

      // Delete existing blocks
      await supabase.from('blocks').delete().eq('page_id', pageId)

      // Insert template blocks
      if (blocks.length > 0) {
        const { error: ie } = await supabase.from('blocks').insert(
          blocks.map((b) => ({
            page_id: pageId,
            type: b.type,
            props: b.props as unknown as Json,
            settings: b.settings as unknown as Json,
            order_index: b.orderIndex,
          }))
        )
        if (ie) throw ie
      }
    },
    onSuccess: (_d, { pageId }) => {
      qc.invalidateQueries({ queryKey: ['blocks', pageId] })
      qc.invalidateQueries({ queryKey: ['pages', siteId] })
    },
  })
}
